import { checkAxiosResponse } from '@mst-fe/shared';
import axios from 'axios';

import { LONG_REQUEST_TIMEOUT } from '../../constants';
import { observeEndpoint } from '../../services/endpoint-observer';
import { axiosOptions, blobAxiosOptions } from '../../services/requests';
import { UsageModel } from './models/usage-model';

export type ResourceActionType = 'NOTIFY_WARN' | 'NOTIFY_CRITICAL' | 'SHUTDOWN';

export interface Units {
  compute: '$';
  egress: 'GB';
  storage: 'GB';
}

export interface LimitValue {
  default: number;
  additional: number;
}

export interface Limits {
  compute: LimitValue;
  egress: LimitValue;
  storage: LimitValue;
}

export interface Usage {
  compute: number;
  egress: number;
  storage: number;
}

export interface OverageUsage {
  compute: number;
  egress: number;
  storage: number;
  computeInPlan: number;
  egressInPlan: number;
  storageInPlan: number;
}

export interface ResourceUsage extends Usage {
  title: string;
  usageCosts: Usage | undefined;
}

export interface UserUsage extends Usage {
  userId: string;
  userEmail: string | undefined;
  username: string | undefined;
  limits: Limits | undefined;
  overageCosts: OverageUsage | undefined;
  usageCosts: Usage | undefined;
}

export interface OrganisationUsage {
  isAdmin: boolean;
  units: Units | undefined;
  limits: Limits | undefined;
  userUsage: UserUsage[] | undefined;
}

export interface UserInstancesUsage {
  units: Units;
  limits: Limits;
  userId: string;
  userEmail: string | undefined;
  username: string | undefined;
  resourceUsage: ResourceUsage[];
}

export interface DetailedUsageCsvExport {
  id: string;
  status: 'REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  finishedAt?: string;
}

export class UsageApi {
  async getUsageLevel(): Promise<{ level: ResourceActionType | undefined } | undefined> {
    return await checkAxiosResponse(
      axios.get<{ level: ResourceActionType | undefined }>('/api/usage/usage-level', axiosOptions(LONG_REQUEST_TIMEOUT))
    );
  }

  getUsageLevelSSE(cb: (value: UsageModel) => void) {
    return observeEndpoint<UsageModel>(`/api/usage/usage-level/sse`, cb);
  }

  async acknowledge(): Promise<void> {
    await checkAxiosResponse(axios.post<unknown>('/api/usage/acknowledge', {}, axiosOptions()));
  }

  async getOrganisationUsage(): Promise<OrganisationUsage | undefined> {
    return await checkAxiosResponse(axios.get<OrganisationUsage | undefined>('/api/usage/summary', axiosOptions(LONG_REQUEST_TIMEOUT)));
  }

  getOrganisationUsageSSE(cb: (value: OrganisationUsage) => void) {
    return observeEndpoint<OrganisationUsage>(`/api/usage/summary/sse`, cb);
  }

  async getOrganisationAggregatedUsageCSVReport(): Promise<Blob | undefined> {
    return await checkAxiosResponse(axios.get<Blob | undefined>('/api/usage/summary-csv-report', blobAxiosOptions()), {
      errorMessage: 'Error while getting Organization Usage CSV Report',
    });
  }

  async requestOrganisationDetailedUsageCSVReport(): Promise<{ id: string; status: string } | undefined> {
    return await checkAxiosResponse(
      axios.get<{ id: string; status: string }>('/api/usage/summary-csv-report', { ...axiosOptions(), params: { detailed: true } }),
      {
        errorMessage: 'Error while requesting Organization Usage CSV Report',
      }
    );
  }

  getOrganisationDetailedUsageCSVReportStatusSSE(exportId: string, cb: (value: { status: string; downloadUrl?: string }) => void) {
    return observeEndpoint<{ status: string; downloadUrl?: string }>(`/api/usage/summary-csv-report/detailed/${exportId}/sse`, cb);
  }

  async getOrganisationDetailedUsageCSVReports(): Promise<DetailedUsageCsvExport[] | undefined> {
    const response = await checkAxiosResponse(
      axios.get<{ data: DetailedUsageCsvExport[] }>('/api/usage/summary-csv-report/detailed', axiosOptions())
    );
    return response?.data;
  }

  async getOrganisationDetailedUsageCSVReportDownloadUrl(exportId: string): Promise<string | undefined> {
    const response = await checkAxiosResponse(
      axios.get<{ downloadUrl?: string }>(`/api/usage/summary-csv-report/detailed/${exportId}`, axiosOptions())
    );
    return response?.downloadUrl;
  }

  async getUserUsageCSVReport(userId: string): Promise<Blob | undefined> {
    return await checkAxiosResponse(axios.get<Blob | undefined>(`/api/usage/summary-csv-report/${userId}`, blobAxiosOptions()), {
      errorMessage: 'Error while getting User Usage CSV Report',
    });
  }

  async getUserInstanceUsage(userId: string): Promise<UserInstancesUsage | undefined> {
    return await checkAxiosResponse(axios.get<UserInstancesUsage | undefined>(`/api/usage/summary/${userId}`, axiosOptions()));
  }
}
